<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { slide } from "svelte/transition";
	import { yyyy, zzzz } from "../../../store.svelte";
	import { toast } from "../../../lib/toast.svelte.ts";
	import Fa from "svelte-fa";
	import {
		faChevronDown,
		faChevronRight,
		faCircleNotch,
		faCheck,
		faTimes,
		faMagnifyingGlass,
		faExclamationCircle,
		faCheckCircle
	} from "@fortawesome/free-solid-svg-icons";

	let {
		expanded = $bindable(),
		control = $bindable(),
		menuActive = $bindable(),
	} = $props();

	// Store for annotation data
	let annotations = $state([]);
	let expandedAnnotations = $state({});
	let loading = $state(true);
	let error = $state(null);

	// Filter states
	let searchTerm = $state("");
	let resolvedFilter = $state("all"); // "all", "resolved", "unresolved"
	let sortOrder = $state("newest"); // "newest", "oldest"

	// Photo modal state
	let showPhotoModal = $state(false);
	let modalPhotoUrl = $state("");
	let modalPhotoCaption = $state("");
	let modalPhotos = $state([]);
	let currentPhotoIndex = $state(0);

	// Filtered annotations based on search and filters
	const filteredAnnotations = $derived(
		annotations
			.filter(annotation => {
				// Filter by resolved status
				if (resolvedFilter === "resolved" && !annotation.is_resolved) return false;
				if (resolvedFilter === "unresolved" && annotation.is_resolved) return false;

				// Filter by search term
				if (searchTerm) {
					const search = searchTerm.toLowerCase();
					return (
						annotation.topic.toLowerCase().includes(search) ||
						annotation.details.toLowerCase().includes(search) ||
						annotation.this_is.toLowerCase().includes(search)
					);
				}

				return true;
			})
			.sort((a, b) => {
				const dateA = new Date(a.created_at);
				const dateB = new Date(b.created_at);
				return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
			})
	);

	// Toggle expansion for a specific annotation and pan map
	const toggleAnnotation = (annotation) => {
		console.log("toggleAnnotation called for:", annotation.annotated_id);
		console.log("annotation.g:", annotation.g);

		const isCurrentlyExpanded = expandedAnnotations[annotation.annotated_id];
		expandedAnnotations = {};
		if (!isCurrentlyExpanded) {
			expandedAnnotations[annotation.annotated_id] = true;
			// Emit event to pan map to annotation location
			if (annotation.g) {
				console.log("Emitting goToAnnotation with GPS data:", annotation.g);
				zzzz.emit('goToAnnotation', annotation.g);
			} else {
				console.warn("No GPS data available for annotation:", annotation.annotated_id);
			}
		}
		expandedAnnotations = { ...expandedAnnotations };
	};

	// Fetch annotations for current livewire only
	const fetchAnnotations = async () => {
		loading = true;
		error = null;

		try {
			if (!yyyy.sesh.logtoken || !yyyy.sesh.activeLivewire) {
				return;
			}

			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
			};

			const wireident = yyyy.sesh.activeLivewire.wireident;
			const response = await fetch(`${url}/annotated?select=*,annotation_photos(*)&wireident=eq.${wireident}&order=created_at.desc`, {
				method: 'GET',
				headers: headers
			});

			if (!response.ok) throw new Error(`Error fetching annotations: ${response.statusText}`);

			const data = await response.json();
			console.log("Fetched annotations:", data);
			console.log("Sample annotation GPS data:", data[0]?.gps);
			annotations = data;

		} catch (err) {
			console.error("Failed to fetch annotations:", err);
			error = err.message;
			toast.error("Failed to load annotations");
		} finally {
			loading = false;
		}
	};

	// Toggle resolved status
	const toggleResolved = async (annotation) => {
		try {
			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
				"Prefer": "return=representation"
			};

			const newResolvedStatus = !annotation.is_resolved;
			const body = JSON.stringify({
				is_resolved: newResolvedStatus,
				resolved_by: newResolvedStatus ? yyyy.sesh.tokeninfo.email : null,
				resolved_at: newResolvedStatus ? new Date().toISOString() : null
			});

			const response = await fetch(`${url}/annotated?annotated_id=eq.${annotation.annotated_id}`, {
				method: 'PATCH',
				headers: headers,
				body: body
			});

			if (!response.ok) throw new Error(`Error updating annotation: ${response.statusText}`);

			// Update local state
			annotations = annotations.map(a =>
				a.annotated_id === annotation.annotated_id
					? { ...a, is_resolved: newResolvedStatus, resolved_by: newResolvedStatus ? yyyy.sesh.tokeninfo.email : null, resolved_at: newResolvedStatus ? new Date().toISOString() : null }
					: a
			);

			toast.success(newResolvedStatus ? "Marked as resolved" : "Marked as unresolved");
		} catch (err) {
			console.error("Failed to update annotation:", err);
			toast.error("Failed to update annotation");
		}
	};

	// Open photo modal
	const openPhotoModal = (photos, index) => {
		modalPhotos = photos;
		currentPhotoIndex = index;
		modalPhotoUrl = photos[index].url;
		modalPhotoCaption = photos[index].caption || "";
		showPhotoModal = true;
	};

	// Navigate photos in modal
	const nextPhoto = () => {
		currentPhotoIndex = (currentPhotoIndex + 1) % modalPhotos.length;
		modalPhotoUrl = modalPhotos[currentPhotoIndex].url;
		modalPhotoCaption = modalPhotos[currentPhotoIndex].caption || "";
	};

	const prevPhoto = () => {
		currentPhotoIndex = (currentPhotoIndex - 1 + modalPhotos.length) % modalPhotos.length;
		modalPhotoUrl = modalPhotos[currentPhotoIndex].url;
		modalPhotoCaption = modalPhotos[currentPhotoIndex].caption || "";
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	onMount(() => {
		zzzz.emit("TriageApplet");
		fetchAnnotations();
	});

	onDestroy(() => {
		zzzz.emit("end Triage Applet");
	});
</script>

<div id="triage-applet" class="card-content">
	<div class="content">
		<h4 class="info-title">Triage Annotations</h4>
		<p class="subtitle-text">Annotations for {yyyy.sesh.activeLivewire.livewire}</p>

		<!-- Filters Section -->
		<div class="filters-container">
			<!-- Search Box -->
			<div class="search-box">
				<input
					type="search"
					bind:value={searchTerm}
					placeholder="Search..."
					class="search-input"
				/>
				<div class="search-icon">
					<Fa icon={faMagnifyingGlass} />
				</div>
			</div>

			<!-- Filter Controls -->
			<div class="filter-controls">
				<div class="filter-group">
					<label>Status:</label>
					<select bind:value={resolvedFilter} class="filter-select">
						<option value="all">All</option>
						<option value="unresolved">Unresolved</option>
						<option value="resolved">Resolved</option>
					</select>
				</div>

				<div class="filter-group">
					<label>Sort:</label>
					<select bind:value={sortOrder} class="filter-select">
						<option value="newest">Newest</option>
						<option value="oldest">Oldest</option>
					</select>
				</div>
			</div>
		</div>

		<!-- Annotations List -->
		{#if loading}
			<div class="loading-state">
				<Fa icon={faCircleNotch} spin size="1.5x" />
				<p>Loading...</p>
			</div>
		{:else if error}
			<div class="error-state">
				<p>Error: {error}</p>
				<button onclick={fetchAnnotations} class="retry-button">Retry</button>
			</div>
		{:else if annotations.length === 0}
			<div class="empty-state">No annotations for this livewire</div>
		{:else if filteredAnnotations.length === 0}
			<div class="empty-state">No annotations match your filters</div>
		{:else}
			<div class="annotations-list">
				{#each filteredAnnotations as annotation (annotation.annotated_id)}
					<div class="annotation-item">
						<div class="annotation-header" onclick={() => toggleAnnotation(annotation)}>
							<span class="expand-icon">
								<Fa icon={expandedAnnotations[annotation.annotated_id] ? faChevronDown : faChevronRight} />
							</span>
							<span class="status-icon">
								<Fa
									icon={annotation.is_resolved ? faCheckCircle : faExclamationCircle}
									color={annotation.is_resolved ? "#48c774" : "#ff9800"}
								/>
							</span>
							<div class="annotation-summary">
								<span class="annotation-topic">{annotation.topic}</span>
								{#if annotation.annotation_photos && annotation.annotation_photos.length > 0}
									<span class="photo-count-badge">{annotation.annotation_photos.length}</span>
								{/if}
							</div>
						</div>

						{#if expandedAnnotations[annotation.annotated_id]}
							<div class="annotation-details" transition:slide={{ duration: 200 }}>
								<!-- Details Section -->
								<div class="detail-section">
									<h5>Details</h5>
									<p class="details-text">{annotation.details}</p>
								</div>

								<!-- Device Info -->
								<div class="detail-section">
									<h5>Device Info</h5>
									<div class="info-item">
										<span class="info-label">Type:</span>
										<span class="info-value">{annotation.this_is}</span>
									</div>
									<div class="info-item">
										<span class="info-label">ID:</span>
										<span class="info-value">{annotation.lw_id}</span>
									</div>
								</div>

								<!-- Created/Resolved Info -->
								<div class="detail-section">
									<h5>Status</h5>
									<div class="info-item">
										<span class="info-label">Created:</span>
										<span class="info-value">{formatDate(annotation.created_at)}</span>
									</div>
									<div class="info-item">
										<span class="info-label">By:</span>
										<span class="info-value">{annotation.created_by}</span>
									</div>
									{#if annotation.is_resolved}
										<div class="info-item">
											<span class="info-label">Resolved:</span>
											<span class="info-value">{formatDate(annotation.resolved_at)}</span>
										</div>
										<div class="info-item">
											<span class="info-label">By:</span>
											<span class="info-value">{annotation.resolved_by || 'N/A'}</span>
										</div>
									{/if}
								</div>

								<!-- Photos Section -->
								{#if annotation.annotation_photos && annotation.annotation_photos.length > 0}
									<div class="detail-section">
										<h5>Photos ({annotation.annotation_photos.length})</h5>
										<div class="photo-gallery">
											{#each annotation.annotation_photos as photo, index}
												<div
													class="photo-thumbnail"
													onclick={() => openPhotoModal(annotation.annotation_photos, index)}
												>
													<img src={photo.url} alt={photo.caption || 'Annotation photo'} />
												</div>
											{/each}
										</div>
									</div>
								{/if}

								<!-- Actions -->
								<div class="annotation-actions">
									<button
										class="action-btn resolve-btn"
										class:is-resolved={annotation.is_resolved}
										onclick={() => toggleResolved(annotation)}
									>
										<Fa icon={annotation.is_resolved ? faTimes : faCheck} />
										{annotation.is_resolved ? 'Mark Unresolved' : 'Mark Resolved'}
									</button>
								</div>
							</div>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

<!-- Photo Modal -->
{#if showPhotoModal}
	<div class="modal-backdrop" onclick={() => showPhotoModal = false}>
		<div class="modal-content" onclick={(e) => e.stopPropagation()}>
			<button class="modal-close" onclick={() => showPhotoModal = false}>
				<Fa icon={faTimes} size="2x" />
			</button>

			<img src={modalPhotoUrl} alt="Full size photo" class="modal-photo" />

			{#if modalPhotoCaption}
				<div class="modal-caption">{modalPhotoCaption}</div>
			{/if}

			{#if modalPhotos.length > 1}
				<button class="modal-nav modal-nav-prev" onclick={prevPhoto}>‹</button>
				<button class="modal-nav modal-nav-next" onclick={nextPhoto}>›</button>
				<div class="modal-counter">{currentPhotoIndex + 1} / {modalPhotos.length}</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	#triage-applet {
		width: 100%;
		padding: 8.775px;
		overflow-y: auto;
		max-height: calc(95vh - 100px);
	}

	.info-title {
		text-align: center;
		margin-bottom: 0.25rem;
	}

	.subtitle-text {
		text-align: center;
		font-size: 0.75rem;
		color: #666;
		margin-bottom: 1rem;
	}

	/* Filters */
	.filters-container {
		margin-bottom: 1rem;
	}

	.search-box {
		position: relative;
		margin-bottom: 0.5rem;
	}

	.search-input {
		width: 100%;
		padding: 0.5rem 2rem 0.5rem 0.75rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.8rem;
		background-color: white;
		caret-color: #ff3e00;
	}

	.search-input:focus {
		outline: none;
		border-color: #ff3e00;
		box-shadow: 0 0 0 2px rgba(255, 62, 0, 0.1);
	}

	.search-icon {
		position: absolute;
		right: 0.5rem;
		top: 50%;
		transform: translateY(-50%);
		color: #999;
		pointer-events: none;
		font-size: 0.8rem;
	}

	.filter-controls {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.filter-group {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		flex: 1;
		min-width: 0;
	}

	.filter-group label {
		font-size: 0.7rem;
		font-weight: 600;
		color: #363636;
		white-space: nowrap;
	}

	.filter-select {
		flex: 1;
		padding: 0.35rem 0.5rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.75rem;
		background-color: white;
		cursor: pointer;
		min-width: 0;
	}

	.filter-select:focus {
		outline: none;
		border-color: #ff3e00;
	}

	/* Annotations List */
	.loading-state, .empty-state, .error-state {
		text-align: center;
		padding: 1.5rem 0.5rem;
		color: #666;
		font-size: 0.85rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
	}

	.error-state {
		color: #ff3860;
	}

	.retry-button {
		padding: 0.4rem 0.8rem;
		border: 1px solid #3273dc;
		background-color: #3273dc;
		color: white;
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.75rem;
		transition: all 0.2s;
	}

	.retry-button:hover {
		background-color: #2366d1;
	}

	.annotations-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.annotation-item {
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		overflow: hidden;
		background-color: white;
	}

	.annotation-header {
		padding: 0.5rem;
		background-color: white;
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: all 0.2s;
		gap: 0.35rem;
	}

	.annotation-header:hover {
		background-color: rgba(255, 62, 0, 0.05);
	}

	.expand-icon {
		width: 12px;
		color: #ff3e00;
		font-size: 0.75rem;
		flex-shrink: 0;
	}

	.status-icon {
		width: 16px;
		font-size: 0.85rem;
		flex-shrink: 0;
	}

	.annotation-summary {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		min-width: 0;
	}

	.annotation-topic {
		font-weight: 600;
		font-size: 0.8rem;
		color: #363636;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		flex: 1;
		min-width: 0;
	}

	.photo-count-badge {
		color: #3273dc;
		font-size: 0.65rem;
		white-space: nowrap;
		flex-shrink: 0;
	}

	/* Annotation Details */
	.annotation-details {
		padding: 0.75rem;
		background-color: rgba(255, 255, 255, 0.98);
		border-top: 1px solid #eee;
	}

	.detail-section {
		margin-bottom: 0.75rem;
	}

	.detail-section:last-child {
		margin-bottom: 0;
	}

	.detail-section h5 {
		font-size: 0.75rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 0.35rem;
	}

	.details-text {
		font-size: 0.75rem;
		color: #4a4a4a;
		line-height: 1.4;
		white-space: pre-wrap;
	}

	.info-item {
		display: flex;
		gap: 0.35rem;
		font-size: 0.7rem;
		margin-bottom: 0.25rem;
	}

	.info-label {
		font-weight: 600;
		color: #666;
	}

	.info-value {
		color: #363636;
	}

	/* Photo Gallery */
	.photo-gallery {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
		gap: 0.35rem;
	}

	.photo-thumbnail {
		position: relative;
		aspect-ratio: 1;
		border-radius: 3px;
		overflow: hidden;
		cursor: pointer;
		transition: transform 0.2s;
	}

	.photo-thumbnail:hover {
		transform: scale(1.05);
	}

	.photo-thumbnail img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	/* Actions */
	.annotation-actions {
		display: flex;
		gap: 0.35rem;
		margin-top: 0.75rem;
		padding-top: 0.75rem;
		border-top: 1px solid #eee;
	}

	.action-btn {
		padding: 0.4rem 0.75rem;
		border-radius: 4px;
		border: 1px solid;
		font-size: 0.75rem;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		gap: 0.35rem;
	}

	.resolve-btn {
		background-color: #48c774;
		border-color: #48c774;
		color: white;
	}

	.resolve-btn:hover {
		background-color: #3ec46d;
	}

	.resolve-btn.is-resolved {
		background-color: #ff9800;
		border-color: #ff9800;
	}

	.resolve-btn.is-resolved:hover {
		background-color: #f59000;
	}

	/* Photo Modal */
	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.85);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10000;
		backdrop-filter: blur(4px);
	}

	.modal-content {
		position: relative;
		max-width: 90vw;
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.modal-close {
		position: absolute;
		top: -40px;
		right: 0;
		background: none;
		border: none;
		color: white;
		cursor: pointer;
		opacity: 0.8;
		transition: opacity 0.2s;
	}

	.modal-close:hover {
		opacity: 1;
	}

	.modal-photo {
		max-width: 100%;
		max-height: 80vh;
		border-radius: 4px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	}

	.modal-caption {
		margin-top: 1rem;
		color: white;
		background-color: rgba(0, 0, 0, 0.6);
		padding: 0.75rem 1rem;
		border-radius: 4px;
		max-width: 80vw;
		text-align: center;
	}

	.modal-nav {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		background-color: rgba(255, 255, 255, 0.2);
		border: none;
		color: white;
		font-size: 3rem;
		width: 60px;
		height: 60px;
		border-radius: 50%;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.modal-nav:hover {
		background-color: rgba(255, 255, 255, 0.3);
	}

	.modal-nav-prev {
		left: 20px;
	}

	.modal-nav-next {
		right: 20px;
	}

	.modal-counter {
		position: absolute;
		bottom: -40px;
		color: white;
		font-size: 0.9rem;
	}
</style>
